<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import { Head } from '@inertiajs/vue3'
import {
    PhCheckCircle, PhClock, PhCreditCard, PhReceipt,
    PhArrowDown, PhWarningCircle,
} from '@phosphor-icons/vue'
import Layout  from '@pages/Shared/layouts/StudentLayout.vue'
import Spinner from '@pages/Shared/ui/Spinner.vue'
import { flashToast } from '@/toast.js'

const props = defineProps({
    student:        Object,
    session:        Object,
    term:           Object,
    feeItems:       Array,   // [{ id, name, amount, amount_paid, status, category }]
    payments:       Array,   // payment history
    paystackKey:    String,
})

const loading       = ref(false)
const paystackReady = ref(false)

onMounted(() => {
    if (typeof window.PaystackPop !== 'undefined') { paystackReady.value = true; return }
    const script   = document.createElement('script')
    script.src     = 'https://js.paystack.co/v1/inline.js'
    script.async   = true
    script.onload  = () => { paystackReady.value = true }
    script.onerror = () => flashToast({ status: false, message: 'Could not load payment gateway. Please refresh.' })
    document.head.appendChild(script)
})

const paystackKeyEnv = import.meta.env.VITE_PAYSTACK_PUBLIC_KEY

const totalFees    = computed(() => props.feeItems?.reduce((s, f) => s + f.amount, 0) ?? 0)
const totalPaid    = computed(() => props.feeItems?.reduce((s, f) => s + (f.amount_paid ?? 0), 0) ?? 0)
const totalBalance = computed(() => totalFees.value - totalPaid.value)
const feeProgress  = computed(() => totalFees.value ? Math.min(100, Math.round((totalPaid.value / totalFees.value) * 100)) : 0)

const overallStatus = computed(() => {
    if (totalBalance.value <= 0) return 'paid'
    if (totalPaid.value > 0)     return 'partial'
    return 'unpaid'
})

const feeItemStatusColor = (status) => ({
    paid:    'bg-green-50 text-green-700',
    partial: 'bg-yellow-50 text-yellow-700',
    unpaid:  'bg-red-50 text-red-600',
}[status] ?? 'bg-gray-100 text-gray-500')

const payWithPaystack = (item) => {
    if (loading.value) return
    const key = paystackKeyEnv || props.paystackKey
    if (!paystackReady.value || !key) {
        flashToast({ status: false, message: 'Payment gateway not ready.' })
        return
    }

    loading.value = true
    const amountDue = item.amount - (item.amount_paid ?? 0)

    try {
        const handler = window.PaystackPop.setup({
            key,
            email:  props.student.email,
            amount: amountDue * 100,
            ref:    `BMC-${item.category?.toUpperCase()}-${Date.now()}`,
            metadata: { student_id: props.student.id, fee_item_id: item.id },
            onClose: () => { loading.value = false },
            callback: (response) => {
                axios.post('/student/payments/verify', {
                    reference:   response.reference,
                    fee_item_id: item.id,
                    category:    item.category,
                })
                .then(({ data }) => {
                    flashToast({ status: true, message: data.message || 'Payment verified!' })
                    setTimeout(() => window.location.reload(), 800)
                })
                .catch((err) => {
                    loading.value = false
                    flashToast({ status: false, message: err?.response?.data?.message || 'Verification failed.' })
                })
            },
        })
        handler.openIframe()
    } catch (e) {
        loading.value = false
        flashToast({ status: false, message: `Payment error: ${e?.message}` })
    }
}

const statusColor = (s) => ({
    paid:    'bg-green-50 text-green-700',
    pending: 'bg-yellow-50 text-yellow-700',
    failed:  'bg-red-50 text-red-600',
}[s] ?? 'bg-gray-100 text-gray-500')
</script>

<template>
    <Layout>
        <Head title="Payments" />

        <div class="mb-8">
            <h1 class="text-2xl font-bold text-gray-900">School Fees</h1>
            <p class="text-gray-500 text-sm mt-1">
                {{ session?.name }} — {{ term?.name }}
            </p>
        </div>

        <!-- Summary strip -->
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div class="bg-white rounded-lg border border-gray-200 p-4 text-center">
                <p class="text-xs text-gray-400 mb-1">Total Fees</p>
                <p class="text-lg font-bold text-gray-900">₦{{ totalFees.toLocaleString() }}</p>
            </div>
            <div class="bg-white rounded-lg border border-green-200 p-4 text-center">
                <p class="text-xs text-gray-400 mb-1">Amount Paid</p>
                <p class="text-lg font-bold text-green-600">₦{{ totalPaid.toLocaleString() }}</p>
            </div>
            <div class="bg-white rounded-lg border border-gray-200 p-4 text-center">
                <p class="text-xs text-gray-400 mb-1">Balance</p>
                <p class="text-lg font-bold"
                    :class="totalBalance > 0 ? 'text-red-500' : 'text-green-600'">
                    ₦{{ totalBalance.toLocaleString() }}
                </p>
            </div>
            <div class="bg-white rounded-lg border border-gray-200 p-4 text-center">
                <p class="text-xs text-gray-400 mb-1">Status</p>
                <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold capitalize"
                    :class="feeItemStatusColor(overallStatus)">
                    {{ overallStatus }}
                </span>
            </div>
        </div>

        <!-- Progress bar -->
        <div class="bg-white rounded-lg border border-gray-200 p-5 mb-6">
            <div class="flex justify-between text-xs text-gray-500 mb-2">
                <span>Payment Progress</span>
                <span class="font-semibold">{{ feeProgress }}% paid</span>
            </div>
            <div class="w-full bg-gray-100 rounded-full h-3">
                <div class="h-3 rounded-full transition-all duration-700"
                    :class="{
                        'bg-green-500':  overallStatus === 'paid',
                        'bg-yellow-400': overallStatus === 'partial',
                        'bg-red-400':    overallStatus === 'unpaid',
                    }"
                    :style="`width: ${feeProgress}%`" />
            </div>
            <p class="text-right text-xs text-gray-400 mt-1">
                ₦{{ totalPaid.toLocaleString() }} of ₦{{ totalFees.toLocaleString() }} paid
            </p>
        </div>

        <!-- Fee breakdown -->
        <div class="bg-white rounded-lg border border-gray-200 overflow-hidden mb-6">
            <div class="px-5 py-4 border-b border-gray-100">
                <h3 class="font-semibold text-gray-800 text-sm">Fee Breakdown</h3>
            </div>
            <div v-if="feeItems?.length" class="divide-y divide-gray-50">
                <div v-for="item in feeItems" :key="item.id"
                    class="px-5 py-4 flex items-center justify-between gap-4">
                    <div class="flex-1">
                        <p class="text-sm font-medium text-gray-800">{{ item.name }}</p>
                        <p class="text-xs text-gray-400 mt-0.5">
                            Paid: ₦{{ Number(item.amount_paid ?? 0).toLocaleString() }}
                            of ₦{{ Number(item.amount).toLocaleString() }}
                        </p>
                    </div>
                    <div class="flex items-center gap-3 shrink-0">
                        <span class="text-xs px-2 py-0.5 rounded-full font-semibold capitalize"
                            :class="feeItemStatusColor(item.status)">
                            {{ item.status }}
                        </span>
                        <!-- Pay button — only for unpaid/partial -->
                        <button
                            v-if="item.status !== 'paid'"
                            @click="payWithPaystack(item)"
                            :disabled="loading || !paystackReady"
                            class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary
                                   hover:bg-primary-light text-white text-xs font-semibold transition
                                   disabled:opacity-50 disabled:cursor-not-allowed">
                            <Spinner v-if="loading" :size="12" />
                            <PhCreditCard v-else :size="13" />
                            {{ item.status === 'partial' ? 'Pay Balance' : 'Pay Now' }}
                        </button>
                        <div v-else
                            class="flex items-center gap-1 text-green-600 text-xs font-semibold">
                            <PhCheckCircle :size="14" />
                            Paid
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="px-5 py-10 text-center text-sm text-gray-400">
                No fee items found for this term.
            </div>
        </div>

        <!-- Payment history -->
        <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div class="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                <PhReceipt :size="16" class="text-primary" />
                <h3 class="font-semibold text-gray-800 text-sm">Payment History</h3>
            </div>
            <div v-if="payments?.length" class="divide-y divide-gray-50">
                <div v-for="p in payments" :key="p.id"
                    class="px-5 py-4 flex items-center justify-between text-sm">
                    <div>
                        <p class="font-medium text-gray-800 capitalize">
                            {{ p.category?.replace(/_/g, ' ') }}
                        </p>
                        <p class="text-xs text-gray-400 mt-0.5">
                            {{ p.reference }} · {{ p.payment_method }}
                            · {{ p.paid_at ? new Date(p.paid_at).toLocaleDateString() : '—' }}
                        </p>
                    </div>
                    <div class="text-right">
                        <p class="font-bold text-gray-900">₦{{ Number(p.amount_paid).toLocaleString() }}</p>
                        <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize"
                            :class="statusColor(p.status)">
                            {{ p.status }}
                        </span>
                    </div>
                </div>
            </div>
            <div v-else class="px-5 py-10 text-center text-sm text-gray-400">
                No payments recorded yet.
            </div>
        </div>
    </Layout>
</template>